<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("menu.app_catalog_shipping_tiers") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button
                        v-if="isAdmin || isSeller"
                        size="md"
                        type="neutral"
                        @click="openCreate"
                    >
                        <i class="fa fa-plus"></i>
                        {{ $t("shipping_tier.btn_add") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="row">
                        <div class="col">
                            <h3 class="mb-0">{{ $t("shipping_tier.title") }}</h3>
                        </div>
                    </div>
                </template>

                <div v-loading="loading">
                    <el-table
                        :data="sortedList"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('shipping_tier.no_data')"
                    >
                        <el-table-column label="ID" prop="id" min-width="60px"></el-table-column>
                        <el-table-column :label="$t('shipping_tier.col_range')" min-width="240px">
                            <template slot-scope="{ row, $index }">
                                {{ rangeLabel(row, $index) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('shipping_tier.col_fee')" min-width="120px">
                            <template slot-scope="{ row }">
                                {{ money(row.fee) }}
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('shipping_tier.col_active')" min-width="100px" align="center">
                            <template slot-scope="{ row }">
                                <el-switch
                                    :value="!!row.active"
                                    @change="onToggleActive(row, $event)"
                                ></el-switch>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('actions')" min-width="120px" align="right">
                            <template slot-scope="{ row }">
                                <base-button
                                    v-if="isAdmin || isSeller"
                                    size="sm"
                                    type="warning"
                                    class="mr-1"
                                    @click="openEdit(row)"
                                >
                                    <i class="fa fa-edit"></i>
                                </base-button>
                                <base-button
                                    v-if="(isAdmin || isSeller) && !isFloor(row)"
                                    size="sm"
                                    type="danger"
                                    @click="confirmDelete(row)"
                                >
                                    <i class="fa fa-trash"></i>
                                </base-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </card>
        </div>

        <!-- Create / Edit Dialog -->
        <el-dialog
            :title="editMode ? $t('shipping_tier.edit_title') : $t('shipping_tier.btn_add')"
            :visible.sync="dialogVisible"
            width="540px"
            @closed="resetForm"
        >
            <el-form
                ref="tierForm"
                :model="form"
                :rules="rules"
                label-width="160px"
            >
                <el-form-item :label="$t('shipping_tier.label_min')" prop="min_subtotal">
                    <el-input-number v-model="form.min_subtotal" :min="0" :step="1"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('shipping_tier.label_fee')" prop="fee">
                    <el-input-number v-model="form.fee" :min="0" :step="1"></el-input-number>
                </el-form-item>
                <el-form-item :label="$t('shipping_tier.label_active')">
                    <el-switch v-model="form.active"></el-switch>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <base-button type="secondary" @click="dialogVisible = false">{{ $t("cancel") || "Cancel" }}</base-button>
                <base-button type="primary" :loading="saving" @click="submitForm">{{ $t("save") }}</base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { apiErrorMessage } from "@/util/api-error";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import { Table, TableColumn, Dialog, Form, FormItem, InputNumber, Switch } from "element-ui";

export default {
    name: "AppCatalogShippingTiers",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Dialog.name]: Dialog,
        [Form.name]: Form,
        [FormItem.name]: FormItem,
        [InputNumber.name]: InputNumber,
        [Switch.name]: Switch,
    },
    data() {
        const validateMin = (rule, value, callback) => {
            if (value === null || value === undefined || value === "") {
                return callback(new Error(this.$t("shipping_tier.field_required")));
            }
            if (value < 0) {
                return callback(new Error(this.$t("shipping_tier.err_min_negative")));
            }
            if (this.isDuplicateMin(value)) {
                return callback(new Error(this.$t("shipping_tier.err_dup_min")));
            }
            return callback();
        };
        const validateFee = (rule, value, callback) => {
            if (value === null || value === undefined || value === "") {
                return callback(new Error(this.$t("shipping_tier.field_required")));
            }
            if (value < 0) {
                return callback(new Error(this.$t("shipping_tier.err_fee_negative")));
            }
            return callback();
        };
        return {
            dialogVisible: false,
            editMode: false,
            saving: false,
            form: {
                id: null,
                min_subtotal: 0,
                fee: 0,
                active: true
            },
            rules: {
                min_subtotal: [{ required: true, validator: validateMin, trigger: "blur" }],
                fee: [{ required: true, validator: validateFee, trigger: "blur" }]
            }
        };
    },
    computed: {
        ...mapGetters({
            list: "ShippingTier/list",
            loading: "ShippingTier/loading",
            isAdmin: "Auth/isAdmin",
            isSeller: "Auth/isSeller"
        }),
        // Defensive DESC sort by min_subtotal (server already returns DESC).
        sortedList() {
            return [...(this.list || [])].sort(
                (a, b) => Number(b.min_subtotal) - Number(a.min_subtotal)
            );
        }
    },
    mounted() {
        this.loadList();
    },
    methods: {
        ...mapActions({
            fetchList: "ShippingTier/fetchList",
            createItem: "ShippingTier/create",
            updateItem: "ShippingTier/update",
            removeItem: "ShippingTier/remove"
        }),
        loadList() {
            this.fetchList({});
        },
        money(val) {
            const n = Number(val || 0);
            return "฿" + n.toLocaleString();
        },
        isFloor(row) {
            return Number(row.min_subtotal) === 0;
        },
        // Render readable range from adjacent thresholds in the DESC-sorted list.
        // index 0 (highest min) => "≥ ฿{min} → ฿{fee}"
        // index i>0 => "฿{min} – ฿{prevTier.min - 1} → ฿{fee}" (prevTier = next-higher row, i-1)
        rangeLabel(row, index) {
            const ge = this.$t("shipping_tier.ge_prefix");
            const to = this.$t("shipping_tier.range_to");
            const arrow = this.$t("shipping_tier.arrow");
            const fee = this.money(row.fee);
            if (index === 0) {
                return ge + this.money(row.min_subtotal) + arrow + fee;
            }
            const prevTier = this.sortedList[index - 1];
            const upper = Number(prevTier.min_subtotal) - 1;
            return this.money(row.min_subtotal) + to + this.money(upper) + arrow + fee;
        },
        // Duplicate check against existing list, excluding the row being edited.
        isDuplicateMin(value) {
            const min = Number(value);
            return (this.list || []).some(
                (t) => Number(t.min_subtotal) === min && t.id !== this.form.id
            );
        },
        openCreate() {
            this.editMode = false;
            this.form = { id: null, min_subtotal: 0, fee: 0, active: true };
            this.dialogVisible = true;
        },
        openEdit(row) {
            this.editMode = true;
            this.form = {
                id: row.id,
                min_subtotal: Number(row.min_subtotal) || 0,
                fee: Number(row.fee) || 0,
                active: !!row.active
            };
            this.dialogVisible = true;
        },
        resetForm() {
            this.form = { id: null, min_subtotal: 0, fee: 0, active: true };
            if (this.$refs.tierForm) {
                this.$refs.tierForm.clearValidate();
            }
        },
        submitForm() {
            this.$refs.tierForm.validate(async (valid) => {
                if (!valid) return;
                this.saving = true;
                try {
                    const payload = {
                        min_subtotal: Number(this.form.min_subtotal),
                        fee: Number(this.form.fee),
                        active: !!this.form.active
                    };
                    if (this.editMode) {
                        await this.updateItem({ id: this.form.id, payload });
                    } else {
                        await this.createItem(payload);
                    }
                    this.dialogVisible = false;
                    this.loadList();
                    this.$notify({ type: "success", message: this.$t("shipping_tier.save_success") });
                } catch (err) {
                    const msg = apiErrorMessage(err, this.$t);
                    this.$notify({ type: "danger", message: msg });
                } finally {
                    this.saving = false;
                }
            });
        },
        async onToggleActive(row, value) {
            try {
                const payload = {
                    min_subtotal: Number(row.min_subtotal),
                    fee: Number(row.fee),
                    active: !!value
                };
                await this.updateItem({ id: row.id, payload });
                this.loadList();
                this.$notify({ type: "success", message: this.$t("shipping_tier.save_success") });
            } catch (err) {
                const msg = apiErrorMessage(err, this.$t);
                this.$notify({ type: "danger", message: msg });
                // Reload to revert the optimistic switch state.
                this.loadList();
            }
        },
        confirmDelete(row) {
            // Floor tier (min_subtotal === 0) must always remain.
            if (this.isFloor(row)) {
                this.$notify({ type: "danger", message: this.$t("shipping_tier.err_floor_protected") });
                return;
            }
            swal.fire({
                title: this.$t("shipping_tier.delete_title"),
                text: this.$t("shipping_tier.delete_msg"),
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                focusCancel: true,
                confirmButtonText: this.$t("app_catalog.btn_delete"),
                cancelButtonText: this.$t("app_catalog.btn_cancel"),
                confirmButtonColor: "#d33",
                cancelButtonColor: "#6c757d"
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.removeItem(row.id);
                        this.loadList();
                        this.$notify({ type: "success", message: this.$t("shipping_tier.delete_success") });
                    } catch (err) {
                        const msg = apiErrorMessage(err, this.$t);
                        this.$notify({ type: "danger", message: msg });
                    }
                }
            });
        }
    }
};
</script>
